/** Manager room (PLAN_05 §P5). A manager opens the personal link emailed by manager-dispatch
 * (EMAIL_JOIN_URL mode): `https://<host>/join/<roomName>?t=<token>`. We connect to LiveKit with the
 * minted token (which carries the manager's display name — no name prompt) and render a functional
 * room: avatar + participant tiles, mic/camera toggle, leave, and chat. P5.1 §C swaps the prebuilt
 * <VideoConference> for <ManagerConference>: the job-worker tile is hidden, tiles show friendly names,
 * and the manager can hide the avatar (the agent stays connected + keeps transcribing). */
import { LiveKitRoom } from "@livekit/components-react";
import { useState } from "react";
import { ManagerConference } from "./ManagerConference.js";

const LIVEKIT_URL = import.meta.env.VITE_LIVEKIT_URL as string | undefined;

/** Parse `/join/<roomName>` + `?t=<token>` from the current URL. */
function parseJoin(): { roomName: string; token: string } {
  const match = window.location.pathname.match(/\/join\/([^/?]+)/);
  const roomName = match?.[1] ? decodeURIComponent(match[1]) : "";
  const token = new URLSearchParams(window.location.search).get("t") ?? "";
  return { roomName, token };
}

function Message({ children }: { children: React.ReactNode }) {
  // Render under the LiveKit theme so the colors track the room (the page background follows
  // `color-scheme`, so a fixed dark text was unreadable on dark mode).
  return (
    <div
      data-lk-theme="default"
      style={{
        display: "grid",
        placeItems: "center",
        height: "100dvh",
        fontFamily: "system-ui, sans-serif",
        background: "var(--lk-bg)",
        color: "var(--lk-fg)",
        padding: "1.5rem",
        textAlign: "center",
      }}
    >
      <p>{children}</p>
    </div>
  );
}

export function App() {
  const { roomName, token } = parseJoin();
  // Once the manager leaves (or the room ends), show a closed state instead of the stale meeting grid.
  const [left, setLeft] = useState(false);

  if (!LIVEKIT_URL) return <Message>Manager room is misconfigured (no LiveKit URL).</Message>;
  if (!roomName || !token) {
    return <Message>This join link is invalid or has expired. Please request a new one.</Message>;
  }
  if (left) {
    return <Message>You've left the session. Thanks — you can close this window.</Message>;
  }

  return (
    <LiveKitRoom
      serverUrl={LIVEKIT_URL}
      token={token}
      connect
      audio
      video={false}
      data-lk-theme="default"
      style={{ height: "100dvh" }}
      onError={(e) => console.error("LiveKit connection error", e)}
      onDisconnected={() => setLeft(true)}
    >
      {/* Custom grid (worker tile filtered, friendly names) + control bar (mic/camera/leave/chat) +
          a "Hide avatar" control — composed from @livekit/components-react primitives. */}
      <ManagerConference />
    </LiveKitRoom>
  );
}
